import React, { useState, useEffect, useRef } from 'react';
import CryptoJS from 'crypto-js';
import { Avatar, FormControlLabel, Checkbox, Link, Grid, Box } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InlineSpinner from 'Components/Commons/Loader/InlineSpinner';
import Api from '../../../Helpers/ApiHandler';
import { loginUser } from '../../../Redux/Auth';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { URL_DASHBOARD, URL_FORGOT_PASSWORD } from 'Helpers/Paths';
import { useDispatch } from 'react-redux';
import { API_LOGIN } from 'Helpers/ApiPaths';
import { Formik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import CustomTextField from 'Components/FormComponents/CustomTextField';
import PasswordTextField from 'Components/FormComponents/PasswordTextField';
import {
    GlobalInputLabelWrapper,
    GlobalInputLabelRequiredWrapper,
    AddButtonWrapper
} from 'Components/Global.style';
import background from '../../../Assets/Images/launch_icons.png';
import TextLogo from '../../../Assets/Images/cms-logo-auth.svg';
import { SERVER_ERROR, VALIDATE_EMAIL } from 'Helpers/Utils';

/**
 * @constant
 * @description API handler instance for making HTTP requests.
 *
 * @type {Api}
 */
const API = new Api({});

/**
 * @constant
 * @description Regular expression for password validation.
 * Requires at least 8 characters with letters, numbers, and special characters.
 *
 * @type {RegExp}
 */
const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*+=._-])[A-Za-z\d!@#$%^&*+=._-]{8,}$/;

/**
 * @component
 * @description Main login component for admin authentication.
 * Provides comprehensive login functionality including:
 * - Email and password authentication
 * - Form validation with Yup schema
 * - Remember me functionality with encrypted storage
 * - Password visibility toggle
 * - Automatic redirect for authenticated users
 * - Loading states and error handling
 * - Responsive design with brand imagery
 * - Redux integration for state management
 * - Encrypted credential storage
 *
 * Key Features:
 * - Secure authentication with encrypted credentials
 * - Remember me functionality with localStorage
 * - Form validation with real-time feedback
 * - Password visibility toggle
 * - Automatic navigation on successful login
 * - Error handling with toast notifications
 * - Responsive two-column layout
 * - Brand imagery and logo display
 * - Redux state management integration
 * - Automatic redirect for logged-in users
 * - CryptoJS encryption for stored credentials
 *
 * @returns {JSX.Element} Complete login interface
 *
 * @example
 * // Used in routing configuration
 * import Login from 'Components/Pages/Login/Login';
 *
 * <Route path="/login" element={<Login />} />
 *
 * @example
 * // Component usage in authentication flow
 * function AuthenticationApp() {
 *   return (
 *     <div>
 *       <Login />
 *     </div>
 *   );
 * }
 *
 * @see API_LOGIN - Login API endpoint
 * @see loginUser - Redux action for user authentication
 * @see URL_DASHBOARD - Dashboard redirect URL
 * @see URL_FORGOT_PASSWORD - Forgot password page URL
 * @see VALIDATE_EMAIL - Email validation regex
 * @see CustomTextField - Styled text field component
 * @see PasswordTextField - Password input component
 */
const Login = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    // eslint-disable-next-line no-unused-vars
    const [email, setEmail] = useState('');
    // eslint-disable-next-line no-unused-vars
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);

    /**
     * @constant
     * @description Initial form values for login form.
     * Contains default empty values for email and password fields.
     *
     * @type {Object}
     * @property {string} email - User email address
     * @property {string} password - User password
     */
    const initialValues = {
        email: '',
        password: ''
    };

    /**
     * @constant
     * @description Yup validation schema for login form.
     * Validates email format and password complexity requirements.
     *
     * @type {yup.ObjectSchema}
     */
    const checkoutSchema = yup.object().shape({
        email: yup
            .string()
            .strict()
            .trim('Cannot include leading and trailing space')
            .required('Email is required')
            .matches(VALIDATE_EMAIL, 'Please enter a valid email')
            .max(150, 'Email must not exceed 150 characters.'),
        password: yup
            .string()
            .strict()
            .trim('Cannot include leading and trailing space')
            .matches(
                passwordRegex,
                'Password must be 8 character and a combination of alphanumeric and special character'
            )
            .required('Please enter password')
    });

    const isLoggedIn = useSelector((state) => state.Auth.isLoggedIn);

    useEffect(() => {
        if (isLoggedIn) {
            navigate('/dashboard');
        }

        const storedEmail = localStorage.getItem('rememberedEmail');
        const storedPassword = localStorage.getItem('rememberedPassword');

        if (storedEmail && storedPassword) {
            setEmail(storedEmail);
            setPassword(storedPassword);
            setRememberMe(true);
        }
    }, [isLoggedIn]);

    /**
     * @function
     * @description Handles form submission for user login.
     * Processes authentication, manages remember me functionality, and handles navigation.
     *
     * @param {Object} values - Form values from Formik
     * @param {string} values.email - User email address
     * @param {string} values.password - User password
     * @returns {void}
     *
     * @example
     * // Called when user submits login form
     * handleSubmit({ email: 'user@example.com', password: 'password123' });
     */
    const handleSubmit = (values) => {
        setLoading(true);

        const loginData = {
            email: values.email.replace(/\s/g, ''),
            password: values.password
        };

        const data = [];
        data['data'] = loginData;

        API.post(API_LOGIN, data)
            .then((response) => {
                try {
                    if (rememberMe) {
                        // eslint-disable-next-line no-undef
                        const encKey = process.env.REACT_APP_ENC_KEY;
                        if (encKey) {
                            localStorage.setItem(
                                'rememberedEmail',
                                CryptoJS.AES.encrypt(response?.data?.email, encKey).toString()
                            );
                            localStorage.setItem(
                                'rememberedPassword',
                                CryptoJS.AES.encrypt(values.password, encKey).toString()
                            );
                        }
                        // If REACT_APP_ENC_KEY is missing (e.g. in production build), skip storing
                        // so login still succeeds instead of throwing and showing "Internal Server Error"
                    } else {
                        localStorage.removeItem('rememberedEmail');
                        localStorage.removeItem('rememberedPassword');
                    }
                } catch (e) {
                    // Don't fail login if remember-me encryption fails (e.g. missing ENC_KEY)
                    console.warn('Remember me storage skipped:', e?.message);
                }
                dispatch(loginUser(response.data));
                setLoading(false);
                navigate(URL_DASHBOARD);
            })
            .catch((error) => {
                setLoading(false);
                toast.error(error?.response?.data?.message || SERVER_ERROR);
            });
    };

    /**
     * @function
     * @description Toggles password visibility state.
     * Switches between showing and hiding password text.
     *
     * @returns {void}
     */
    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    /**
     * @function
     * @description Prevents default mouse down behavior on password field.
     * Prevents focus loss when clicking password visibility toggle.
     *
     * @param {Event} event - Mouse down event
     * @returns {void}
     */
    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const ref = useRef();

    useEffect(() => {
        const storedEmail = localStorage.getItem('rememberedEmail');
        const storedPassword = localStorage.getItem('rememberedPassword');

        if (storedEmail && storedPassword) {
            ref.current.setFieldValue(
                'email',
                CryptoJS.AES.decrypt(
                    localStorage.getItem('rememberedEmail'),
                    // eslint-disable-next-line no-undef
                    process.env.REACT_APP_ENC_KEY
                ).toString(CryptoJS.enc.Utf8)
            );
            ref.current.setFieldValue(
                'password',
                CryptoJS.AES.decrypt(
                    localStorage.getItem('rememberedPassword'),
                    // eslint-disable-next-line no-undef
                    process.env.REACT_APP_ENC_KEY
                ).toString(CryptoJS.enc.Utf8)
            );
        }
    }, []);

    return (
        <Grid container component="main" sx={{ height: '100vh', backgroundColor: '#F5E0B5' }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexGrow: 1,
                    flexBasis: '50%',
                    height: '100%' // Ensure the parent container has a height defined
                }}>
                <img
                    loading="lazy"
                    decoding="async"
                    src={background}
                    alt="Bluecow Divine Connect"
                    style={{
                        maxWidth: '60%',
                        maxHeight: '90%',
                        objectFit: 'contain',
                        borderRadius: '12px'
                    }}
                />
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexGrow: 1,
                    flexBasis: '50%',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                <Box
                    sx={{
                        my: 6,
                        mx: 6,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        backgroundColor: '#FFFFFF',
                        padding: '20px',
                        height: '490px',
                        borderRadius: '5px'
                    }}>
                    <Avatar sx={{ m: 1, bgcolor: '#F5E0B5' }}>
                        <LockOutlinedIcon />
                    </Avatar>
                    {/* <Typography component="h1" variant="body2">
                        DEVOTEE
                    </Typography> */}
                    <img loading="lazy" decoding="async" src={TextLogo} height={40} alt="Bluecow CMS" />
                    <Formik
                        innerRef={ref}
                        onSubmit={handleSubmit}
                        initialValues={initialValues}
                        validationSchema={checkoutSchema}
                        validateOnChange={true}
                        validateOnBlur={false}>
                        {({ values, errors, touched, handleBlur, handleChange, handleSubmit }) => (
                            <form onSubmit={handleSubmit}>
                                <Box style={{ marginTop: '20px', width: '500px' }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <GlobalInputLabelWrapper>
                                                Email{' '}
                                                <GlobalInputLabelRequiredWrapper>
                                                    {' '}
                                                    *{' '}
                                                </GlobalInputLabelRequiredWrapper>
                                            </GlobalInputLabelWrapper>
                                            <CustomTextField
                                                name="email"
                                                type="text"
                                                placeholder={'Email'}
                                                value={values.email}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                error={!!touched.email && !!errors.email}
                                                helperText={touched.email && errors.email}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <GlobalInputLabelWrapper>
                                                Password{' '}
                                                <GlobalInputLabelRequiredWrapper>
                                                    {' '}
                                                    *{' '}
                                                </GlobalInputLabelRequiredWrapper>
                                            </GlobalInputLabelWrapper>
                                            <PasswordTextField
                                                size="small"
                                                showPassword={showPassword}
                                                props={{ placeholder: 'Password' }}
                                                margin="normal"
                                                fullWidth
                                                name="password"
                                                type={showPassword ? 'text' : 'password'}
                                                id="password"
                                                handleClickShowPassword={handleClickShowPassword}
                                                handleMouseDownPassword={handleMouseDownPassword}
                                                value={values.password}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                error={!!touched.password && !!errors.password}
                                                helperText={touched.password && errors.password}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container>
                                        <Grid item xs>
                                            <FormControlLabel
                                                componentsProps={{
                                                    typography: { variant: 'body2' }
                                                }}
                                                control={
                                                    <Checkbox
                                                        checked={rememberMe}
                                                        variant="body2"
                                                        value="remember"
                                                        onChange={(e) =>
                                                            setRememberMe(e.target.checked)
                                                        }
                                                        color="primary"
                                                    />
                                                }
                                                label="Remember me"
                                            />
                                        </Grid>
                                        <Grid mt={1} item>
                                            <Link
                                                href={URL_FORGOT_PASSWORD}
                                                underline="none"
                                                variant="body2">
                                                Forgot password?
                                            </Link>
                                        </Grid>
                                    </Grid>
                                    <AddButtonWrapper
                                        type="submit"
                                        fullWidth
                                        disabled={loading}
                                        sx={{ mt: 3, mb: 2 }}>
                                        {loading ? (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: 1
                                                }}>
                                                <InlineSpinner size={22} color="inherit" />
                                                Signing in…
                                            </Box>
                                        ) : (
                                            'Sign In'
                                        )}
                                    </AddButtonWrapper>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            </Box>
        </Grid>
    );
};

export default Login;
